import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api/client";
import TopBar from "../components/layout/TopBar";
import { Button } from "../components/ui/button";
import { CheckCheck, Trash2, Bell, ExternalLink } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);

  const load = () => API.get("/notifications").then(r => setNotifications(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    await API.put(`/notifications/${id}/read`);
    load();
  };

  const markAllRead = async () => {
    await API.put("/notifications/read-all");
    toast.success("All marked as read");
    load();
  };

  const deleteNotif = async (id) => {
    try {
      await API.delete(`/notifications/${id}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cannot delete");
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <>
      <TopBar title="Notifications" />
      <div className="flex-1 overflow-y-auto p-6" data-testid="notifications-page">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-[#686B74]">{unreadCount} unread notification{unreadCount !== 1 ? "s" : ""}</p>
          {unreadCount > 0 && (
            <Button data-testid="mark-all-read-btn" variant="outline" size="sm" onClick={markAllRead} className="text-xs">
              <CheckCheck className="w-3.5 h-3.5 mr-1.5" /> Mark all as read
            </Button>
          )}
        </div>

        <div className="bg-white border border-[#EBEBEB] rounded-md divide-y divide-[#EBEBEB]">
          {notifications.length === 0 && (
            <div className="px-4 py-12 text-center">
              <Bell className="w-8 h-8 text-[#EBEBEB] mx-auto mb-2" />
              <p className="text-sm text-[#686B74]">No notifications</p>
            </div>
          )}
          {notifications.map((n) => (
            <div
              key={n.id}
              data-testid={`notification-${n.id}`}
              className={`flex items-start gap-3 px-4 py-3 hover:bg-[#F9F9FB] transition-colors ${!n.read ? "bg-[#EDF0FF]/20" : ""}`}
            >
              <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${!n.read ? "bg-[#4353FF]" : "bg-transparent"}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#111216]">{n.message}</p>
                <div className="flex items-center gap-2 mt-1">
                  <button
                    className="text-xs text-[#4353FF] hover:underline flex items-center gap-1"
                    onClick={() => navigate(`/tasks/${n.task_id}`)}
                  >
                    {n.task_title} <ExternalLink className="w-2.5 h-2.5" />
                  </button>
                  <span className="text-[11px] text-[#686B74]">
                    {n.created_at ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true }) : ""}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {!n.read && (
                  <Button variant="ghost" size="sm" onClick={() => markRead(n.id)} className="h-7 text-xs px-2" data-testid={`mark-read-${n.id}`}>
                    Read
                  </Button>
                )}
                {n.read && (
                  <Button variant="ghost" size="sm" onClick={() => deleteNotif(n.id)} className="h-7 text-xs px-2 text-[#E24A4A]" data-testid={`delete-notif-${n.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
